/**
 * Whether a stored contract document address can actually be opened by an
 * agent in a browser.
 *
 * Contracts moved to the document store under CWA-118, and those object
 * addresses are private: opening one gives an access-denied page, so they are
 * shown as copyable references instead of links (FR-013). Documents recorded
 * before the move still live in the shared drive and open normally, as do
 * addresses that carry their own temporary access signature — those are worth
 * offering as links.
 *
 * The check is a deliberate allow-list: an address we do not recognise is
 * treated as not openable, because a link that errors is worse than no link.
 */
export const isOpenableDocumentUrl = (
  url?: string | null
): boolean => {
  if (!url) return false;

  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return false;
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;

  const host = parsed.hostname.toLowerCase();
  if (host === "docs.google.com" || host === "drive.google.com") return true;

  // A pre-signed address carries its own access grant, so it opens even though
  // the plain object address does not.
  return parsed.searchParams.has("X-Amz-Signature");
};
